import { Button } from "@ui/Button";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Modal } from "@ui/molecules/Modal/Modal";
import { toast } from "@ui/molecules/Toasts/toast";
import { RadioGroup } from "@ui/RadioGroup";
import { HStack, VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { getErrorMessage } from "@utils/getErrorMessage";
import { createSignal, Show } from "solid-js";
import { reportVideo } from "@/client";
import { useOffersVideoReport } from "./offersVideoReport";
import { REPORT_REASONS, type ReportReason } from "./reportReasons";

type Props = {
	videoId: string;
};

export const ReportVideoButton = (props: Props) => {
	const offered = useOffersVideoReport();
	const [open, setOpen] = createSignal(false);
	const [reason, setReason] = createSignal<ReportReason | null>(null);
	const [note, setNote] = createSignal("");
	const [sending, setSending] = createSignal(false);

	const close = () => {
		setOpen(false);
		setReason(null);
		setNote("");
	};

	const send = async () => {
		const chosen = reason();
		if (chosen === null || sending()) {
			return;
		}

		setSending(true);
		const response = await reportVideo({
			path: { video_id: props.videoId },
			body: { reason: chosen, note: note().trim() || null },
		});
		setSending(false);
		if (response.error) {
			toast.error({
				title: "Could not send your report.",
				description: getErrorMessage(response.error),
			});
			return;
		}

		toast.success({
			title: "Report sent.",
			description: "Thank you. We will review this video.",
		});
		close();
	};

	return (
		<Show when={offered()}>
			<button
				type="button"
				class="circle-icon-button"
				aria-label="Report video"
				onClick={(e) => {
					e.stopPropagation();
					setOpen(true);
				}}
			>
				<div class="circle-icon">
					<MaterialSymbol symbol="flag" />
				</div>
				<Text color="white" size="small">
					Report
				</Text>
			</button>
			<Modal open={open} onClose={close} title="Report this video" size="small">
				<VStack spacing="large">
					<RadioGroup
						value={reason() ?? ""}
						onChange={(value) => setReason(value as ReportReason)}
						options={REPORT_REASONS}
						orientation="vertical"
						variant="card"
						ariaLabel="Reason for reporting"
						name="video-report-reason"
					/>
					<textarea
						value={note()}
						onInput={(event) => setNote(event.currentTarget.value)}
						placeholder="Anything else we should know? (optional)"
						maxLength={1000}
						rows={3}
						aria-label="Details"
					/>
					<HStack spacing="small" justify="end">
						<Button variant="secondary" theme="monochrome" onClick={close}>
							Cancel
						</Button>
						<Button
							variant="primary"
							disabled={reason() === null || sending()}
							onClick={() => void send()}
						>
							{sending() ? "Sending…" : "Send report"}
						</Button>
					</HStack>
				</VStack>
			</Modal>
		</Show>
	);
};
